import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const TOKEN_PATTERN =
  /((?:https?:\/\/|www\.)[^\s<]+)|([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})|(#([a-zA-Z0-9_]{2,50}))/gi;

const RichText = styled.div<{ $compact?: boolean }>`
  display: grid;
  gap: ${({ $compact }) => ($compact ? '0.45rem' : '0.55rem')};
  color: color-mix(in srgb, var(--onSurfaceVariant) 92%, var(--onSurface));
  font-size: ${({ $compact }) => ($compact ? '0.875rem' : '0.92rem')};
  line-height: 1.65;

  p {
    margin: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
`;

const InlineLink = styled.a`
  color: var(--primary);
  font-weight: 700;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const HashtagLink = styled(Link)`
  color: var(--primary);
  font-weight: 700;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const MediaStack = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.7rem;
`;

const MediaLink = styled.a`
  display: block;
  text-decoration: none;
`;

const MediaImage = styled.img`
  width: 100%;
  max-height: 340px;
  object-fit: cover;
  border-radius: 18px;
  border: 1px solid color-mix(in srgb, var(--outline) 74%, transparent);
  background: color-mix(in srgb, var(--surfaceVariant, var(--surface)) 65%, transparent);
`;

const MediaVideo = styled.video`
  width: 100%;
  max-height: 380px;
  border-radius: 18px;
  border: 1px solid color-mix(in srgb, var(--outline) 74%, transparent);
  background: #000;
`;

const normalizeExternalUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const trimTrailingPunctuation = (value: string) => {
  let url = value;
  let trailing = '';
  while (/[),.!?;:]$/.test(url)) {
    trailing = `${url.slice(-1)}${trailing}`;
    url = url.slice(0, -1);
  }
  return { url, trailing };
};

const renderLinkedLine = (line: string, stopPropagation?: boolean) => {
  const matches = Array.from(line.matchAll(TOKEN_PATTERN));
  if (!matches.length) return line;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  matches.forEach((match, index) => {
    const rawMatch = match[0] ?? '';
    const startIndex = match.index ?? 0;
    if (startIndex > lastIndex) {
      parts.push(line.slice(lastIndex, startIndex));
    }

    const rawUrl = match[1];
    const rawEmail = match[2];
    const hashtagToken = match[3];
    const hashtagTag = match[4];

    if (hashtagToken && hashtagTag) {
      const tag = hashtagTag.toLowerCase();
      parts.push(
        <HashtagLink
          key={`tag-${tag}-${index}-${startIndex}`}
          to={`/search?q=${encodeURIComponent(`#${tag}`)}`}
          onClick={(event) => {
            if (stopPropagation) event.stopPropagation();
          }}
        >
          {hashtagToken}
        </HashtagLink>
      );
      lastIndex = startIndex + rawMatch.length;
      return;
    }

    const rawLink = rawUrl || rawEmail || '';
    const { url, trailing } = trimTrailingPunctuation(rawLink);
    const href = rawEmail ? `mailto:${url}` : normalizeExternalUrl(url);
    parts.push(
      <InlineLink
        key={`${href}-${index}`}
        href={href}
        target={rawEmail ? undefined : '_blank'}
        rel={rawEmail ? undefined : 'noopener noreferrer'}
        onClick={(event) => {
          if (stopPropagation) event.stopPropagation();
        }}
      >
        {url}
      </InlineLink>
    );
    if (trailing) {
      parts.push(trailing);
    }
    lastIndex = startIndex + rawMatch.length;
  });

  if (lastIndex < line.length) {
    parts.push(line.slice(lastIndex));
  }

  return parts;
};

const normalizeImages = (images?: Array<string | null | undefined> | null) =>
  (images ?? [])
    .map((image) => (typeof image === 'string' ? image.trim() : ''))
    .filter((image): image is string => image.length > 0);

type PostRichContentProps = {
  text?: string | null;
  images?: Array<string | null | undefined> | null;
  videoUrl?: string | null;
  compact?: boolean;
  imageAlt?: string;
  stopPropagation?: boolean;
};

export const PostRichContent: React.FC<PostRichContentProps> = ({
  text,
  images,
  videoUrl,
  compact = false,
  imageAlt = 'Post image',
  stopPropagation = false,
}) => {
  const normalizedImages = normalizeImages(images);
  const normalizedVideoUrl = typeof videoUrl === 'string' && videoUrl.trim() ? videoUrl.trim() : '';
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter((line, index, arr) => line.length > 0 || (arr.length === 1 && index === 0));

  return (
    <MediaStack>
      {lines.length ? (
        <RichText $compact={compact}>
          {lines.map((line, index) => (
            <p key={`${index}-${line.slice(0, 20)}`}>{renderLinkedLine(line, stopPropagation)}</p>
          ))}
        </RichText>
      ) : null}

      {normalizedImages.length ? (
        <MediaGrid>
          {normalizedImages.map((imageUrl, index) => (
            <MediaLink
              key={`${imageUrl}-${index}`}
              href={imageUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => {
                if (stopPropagation) event.stopPropagation();
              }}
            >
              <MediaImage src={imageUrl} alt={normalizedImages.length > 1 ? `${imageAlt} ${index + 1}` : imageAlt} />
            </MediaLink>
          ))}
        </MediaGrid>
      ) : null}

      {normalizedVideoUrl ? (
        <MediaVideo
          src={normalizedVideoUrl}
          controls
          onClick={(event) => {
            if (stopPropagation) event.stopPropagation();
          }}
        />
      ) : null}
    </MediaStack>
  );
};

export default PostRichContent;
